import { useEffect, useRef, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePagination } from '@/hooks/usePagination';
import { cn } from '@/lib/utils';

interface PaginatedReaderProps {
  children: ReactNode;
  className?: string;
}

/**
 * E-reader/tablet display mode for a wiki article or publication
 * section: slices already-rendered content into fixed-height "book
 * pages" inside a fixed-height viewport (see `usePagination` for how
 * pages are measured), with:
 *
 *  - **Tap zones**: the left third of the reader goes one page back,
 *    the right third goes one page forward (standard e-reader
 *    convention — Kindle, Apple Books, etc. all use this "thirds"
 *    layout). The middle third is left inert so taps on links, star
 *    buttons, highlighted text, etc. inside the content still work
 *    without accidentally turning the page.
 *  - **Keyboard**: Left/Right and PageUp/PageDown, so the reader isn't
 *    exclusively touch-first on a tablet with a keyboard attached.
 *  - **A page indicator** ("3 of 12") and edge chevrons, for
 *    discoverability — the tap zones themselves are invisible, so
 *    first-time users need some visual affordance that paging exists.
 *
 * `children` should be exactly the same content a normal (scrolling)
 * article view would render — this component only changes how it's
 * *laid out and navigated*, never re-renders it differently.
 */
export function PaginatedReader({ children, className }: PaginatedReaderProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { page, setPage, pageCount, offset } = usePagination(viewportRef, contentRef);

  const canGoBack = page > 0;
  const canGoForward = page < pageCount - 1;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setPage((p) => Math.max(0, p - 1));
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        setPage((p) => Math.min(pageCount - 1, p + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageCount, setPage]);

  return (
    <div className={cn('relative flex min-h-0 flex-col', className)}>
      <div
        ref={viewportRef}
        className="relative min-h-0 flex-1 overflow-hidden"
        role="group"
        aria-roledescription="paginated reader"
        aria-label={`Page ${page + 1} of ${pageCount}`}
      >
        {/* The content flows normally here — one plain column at its
            natural height. Only `translateY` moves; nothing about the
            content's own layout is touched, which is what makes the
            measured page offsets in `usePagination` match exactly
            what's on screen. */}
        <div
          ref={contentRef}
          className="px-4 will-change-transform"
          style={{
            transform: `translateY(-${offset}px)`,
            transition: 'transform 200ms ease',
          }}
        >
          {children}
        </div>

        {/* Invisible tap zones — left third (back) / right third
            (forward). The middle third is deliberately left uncovered
            so clicks there reach links/buttons in the content. */}
        <button
          type="button"
          aria-label="Previous page"
          disabled={!canGoBack}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="absolute inset-y-0 left-0 z-10 w-1/3 cursor-w-resize disabled:cursor-default"
        />
        <button
          type="button"
          aria-label="Next page"
          disabled={!canGoForward}
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          className="absolute inset-y-0 right-0 z-10 w-1/3 cursor-e-resize disabled:cursor-default"
        />

        {canGoBack && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex w-8 items-center justify-center">
            <ChevronLeft className="size-5 text-muted-foreground/40" />
          </div>
        )}
        {canGoForward && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center">
            <ChevronRight className="size-5 text-muted-foreground/40" />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-center border-t border-border py-2 text-xs text-muted-foreground">
        Page {page + 1} of {pageCount}
      </div>
    </div>
  );
}
